import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, Tag, Home, ChevronRight, Loader2, AlertCircle, Inbox,
  Phone, ArrowLeft, Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { apiFetch } from '../utils/api';

// Color palette for category cards — cycles through these
const CARD_PALETTE = [
  { ring: 'border-indigo-200', bg: 'from-indigo-50 to-white', bar: 'bg-indigo-500', text: 'text-indigo-600' },
  { ring: 'border-purple-200', bg: 'from-purple-50 to-white', bar: 'bg-purple-500', text: 'text-purple-600' },
  { ring: 'border-sky-200',    bg: 'from-sky-50 to-white',    bar: 'bg-sky-500',    text: 'text-sky-600'    },
  { ring: 'border-teal-200',   bg: 'from-teal-50 to-white',   bar: 'bg-teal-500',   text: 'text-teal-600'   },
  { ring: 'border-emerald-200',bg: 'from-emerald-50 to-white',bar: 'bg-emerald-500',text: 'text-emerald-600'},
  { ring: 'border-amber-200',  bg: 'from-amber-50 to-white',  bar: 'bg-amber-500',  text: 'text-amber-600'  },
  { ring: 'border-rose-200',   bg: 'from-rose-50 to-white',   bar: 'bg-rose-500',   text: 'text-rose-600'   },
];

// Response shape from Pulse's /api/analytics/taxonomy/drill (proxied via
// /api/customer-support/categories):
//   { level: "l1"|"l2"|"l3"|"l4", items: [{name, count, positive?, negative?, ...}], total }
function extractDrillResponse(data) {
  if (!data) return { items: [], total: 0, level: 'L1' };
  const list = Array.isArray(data.items) ? data.items
            : Array.isArray(data.l1_categories) ? data.l1_categories.map(n => ({ name: n }))  // legacy
            : Array.isArray(data) ? data
            : [];
  const total = data.total ?? list.reduce((s, c) => s + (c.count || 0), 0);
  const level = (data.level || 'l1').toUpperCase();
  return {
    items: list.map(c => {
      if (typeof c === 'string') return { name: c, count: 0, percentage: 0 };
      const count = c.count ?? c.calls ?? 0;
      return {
        name: c.name || c.category || c.label || 'Unknown',
        count,
        percentage: c.percentage ?? (total ? Math.round((count / total) * 1000) / 10 : 0),
      };
    }),
    total,
    level,
    leaves_at_parent: data.leaves_at_parent,
  };
}

export default function CustomerSupportPage({ brand: brandProp }) {
  const params = useParams();
  const brand = brandProp || params.brandName ? decodeURIComponent(brandProp || params.brandName) : null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [skus, setSkus] = useState([]);
  const [skuQuery, setSkuQuery] = useState('');
  const [activeSku, setActiveSku] = useState(searchParams.get('sku') || null);
  // path is an ordered list of categories the user has drilled into, e.g. ['Logistics/Warehouse']
  const initPath = (searchParams.get('path') || '').split('>').filter(Boolean);
  const [path, setPath] = useState(initPath);

  const [skusLoading, setSkusLoading] = useState(false);
  const [catsLoading, setCatsLoading] = useState(false);
  const [cats, setCats] = useState({ items: [], total: 0, level: 'L1' });
  const [rawResponse, setRawResponse] = useState(null);  // kept for debug peek
  const [error, setError] = useState(null);

  // Sync activeSku + path into URL
  useEffect(() => {
    const sp = new URLSearchParams();
    if (activeSku) sp.set('sku', activeSku);
    if (path.length) sp.set('path', path.join('>'));
    setSearchParams(sp, { replace: true });
  }, [activeSku, path, setSearchParams]);

  // Fetch this brand's SKUs once
  useEffect(() => {
    if (!brand) return;
    let cancelled = false;
    (async () => {
      setSkusLoading(true);
      try {
        const res = await apiFetch(`/api/customer-support/skus?brand=${encodeURIComponent(brand)}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setSkus(Array.isArray(data.items) ? data.items : []);
        }
      } finally {
        if (!cancelled) setSkusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand]);

  // Fetch level data — Pulse drill is server-side. New request on every drill.
  const fetchCategories = useCallback(async () => {
    if (!activeSku || !brand) return;
    setCatsLoading(true);
    setError(null);
    try {
      const sp = new URLSearchParams({ brand, sku: activeSku });
      // Pass the current drill path so the backend can populate l1/l2/l3 params on Pulse
      if (path.length) sp.set('parent', JSON.stringify(path));
      const res = await apiFetch(`/api/customer-support/categories?${sp.toString()}`);
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json())?.detail || detail; } catch (_) { /* keep default */ }
        setError({ status: res.status, message: detail });
        setCats({ items: [], total: 0, level: `L${path.length + 1}` });
        setRawResponse(null);
        if (res.status === 503)      toast.error('Customer Support is not configured yet. Set CALLINSIGHT_URL and API_KEY in deployment secrets.', { duration: 8000 });
        else if (res.status === 401) toast.error('CallInsight rejected the API key.', { duration: 6000 });
        else                          toast.error(`Failed to load categories: ${detail}`, { duration: 6000 });
        return;
      }
      const data = await res.json();
      setRawResponse(data);
      setCats(extractDrillResponse(data));
    } catch (e) {
      setError({ status: 0, message: String(e?.message || e) });
      setCats({ items: [], total: 0, level: `L${path.length + 1}` });
      setRawResponse(null);
    } finally {
      setCatsLoading(false);
    }
  }, [activeSku, brand, path]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const filteredSkus = useMemo(() => {
    const q = skuQuery.trim().toLowerCase();
    if (!q) return skus;
    return skus.filter(s => s.sku_name.toLowerCase().includes(q));
  }, [skus, skuQuery]);

  const selectedSkuMeta = useMemo(
    () => skus.find(s => s.sku_name === activeSku) || null,
    [skus, activeSku]
  );

  const drill = (categoryName) => setPath(prev => [...prev, categoryName]);
  const goHome = () => setPath([]);
  const goToBreadcrumb = (idx) => setPath(prev => prev.slice(0, idx + 1));

  // Empty-state: no SKU selected yet
  if (!activeSku) {
    return (
      <div className="space-y-6" data-testid="customer-support-page">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">Customer Support · {brand}</h1>
          <p className="text-muted-foreground mt-1 text-sm">Pick a product to see how its customer-support calls are distributed across issue categories.</p>
        </div>
        <Card className="p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search products" value={skuQuery} onChange={e => setSkuQuery(e.target.value)} data-testid="sku-search" />
          </div>
          {skusLoading && <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading products…</div>}
          {!skusLoading && filteredSkus.length === 0 && (
            <div className="py-10 text-center text-muted-foreground">
              <Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No products tracked for {brand}.
            </div>
          )}
          {!skusLoading && filteredSkus.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredSkus.map(s => (
                <button
                  key={s.sku_name}
                  onClick={() => { setActiveSku(s.sku_name); setPath([]); }}
                  className="text-left flex items-center gap-3 p-3 rounded-xl border border-border hover:border-[#8b5cf6]/40 hover:bg-[#8b5cf6]/5 transition-colors"
                  data-testid={`sku-card-${s.sku_name}`}
                >
                  {s.image_url ? (
                    <img src={s.image_url} alt="" className="w-11 h-11 rounded-lg object-cover bg-accent" />
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-[#8b5cf6]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.sku_name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-1">
                      {s.channels.slice(0, 3).map(c => <Badge key={c} variant="secondary" className="text-[9px] h-4 px-1">{c}</Badge>)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  }

  // SKU-detail view — product header + Category Distribution
  return (
    <div className="space-y-5" data-testid="customer-support-page">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setActiveSku(null); setPath([]); }} className="gap-1.5 -ml-2" data-testid="back-to-products-btn">
          <ArrowLeft className="w-4 h-4" /> Back to products
        </Button>
      </div>

      {/* Product header card */}
      <Card className="p-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/10 flex items-center justify-center shrink-0">
          {selectedSkuMeta?.image_url
            ? <img src={selectedSkuMeta.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
            : <Package className="w-6 h-6 text-[#8b5cf6]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold leading-tight">{brand} - {activeSku}</div>
          <div className="text-sm text-muted-foreground mt-1">Click a category to drill into L2, L3, then the calls themselves.</div>
        </div>
      </Card>

      {/* Category Distribution */}
      <Card className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center shrink-0">
            <Tag className="w-5 h-5 text-[#8b5cf6]" />
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold">Category Distribution</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {cats.total.toLocaleString()} calls categorized · Showing {cats.level || `L${path.length + 1}`} breakdown
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="category-breadcrumb">
          <button onClick={goHome} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${path.length === 0 ? 'bg-accent border-border font-medium' : 'border-border/50 hover:bg-accent/50'}`}>
            <Home className="w-3 h-3" /> All Categories
          </button>
          {path.map((p, i) => (
            <React.Fragment key={i}>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              <button
                onClick={() => goToBreadcrumb(i)}
                className={`px-2 py-1 rounded-md border ${i === path.length - 1 ? 'bg-accent border-border font-medium' : 'border-border/50 hover:bg-accent/50'}`}
              >
                {p}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-4 py-3 flex items-start gap-2 text-sm" data-testid="error-banner">
            <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-rose-300">Unable to load categories ({error.status || 'network'})</div>
              <div className="text-rose-200/80 text-xs mt-0.5">{error.message}</div>
            </div>
          </div>
        )}

        {/* Cards grid OR calls list at deepest level */}
        {catsLoading && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading categories…
          </div>
        )}

        {!catsLoading && !error && cats.items.length === 0 && (
          <div className="py-8 text-center text-muted-foreground space-y-3" data-testid="no-categories">
            <Phone className="w-6 h-6 mx-auto opacity-50" />
            <div className="font-medium text-sm">No calls categorized at this level yet.</div>
            {rawResponse && (
              <details className="text-left max-w-2xl mx-auto bg-slate-50 dark:bg-slate-900/30 rounded-lg p-3 border border-border/50">
                <summary className="cursor-pointer text-xs text-[#8b5cf6] font-medium hover:underline select-none">
                  Show raw CallInsight response (debug)
                </summary>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  <div className="mb-2">
                    Queried with: <code className="px-1 rounded bg-slate-200/50 dark:bg-slate-800/50">brand={brand}, sku={activeSku}, path={JSON.stringify(path)}</code>
                  </div>
                  <pre className="text-[10px] overflow-auto max-h-64 whitespace-pre-wrap leading-relaxed">{JSON.stringify(rawResponse, null, 2)}</pre>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    If CallInsight returned data but the SKU above doesn&apos;t match what CallInsight indexes by, the SKU naming differs between the two apps. Fix the SKU name in CallInsight (or in AURA&apos;s tracked SKU list) so they align.
                  </div>
                </div>
              </details>
            )}
          </div>
        )}

        {!catsLoading && cats.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cats.items.map((c, idx) => {
              const p = CARD_PALETTE[idx % CARD_PALETTE.length];
              const pct = Number(c.percentage) || 0;
              return (
                <button
                  key={`${c.name}-${idx}`}
                  onClick={() => drill(c.name)}
                  className={`relative text-left rounded-2xl border ${p.ring} bg-gradient-to-br ${p.bg} p-4 hover:shadow-md hover:-translate-y-0.5 transition-all`}
                  data-testid={`category-card-${c.name}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-slate-700 truncate pr-2" title={c.name}>{c.name}</div>
                    <div className={`text-xl font-bold ${p.text} font-variant-numeric tabular-nums`}>{pct}%</div>
                  </div>
                  <div className="h-1.5 mt-3 rounded-full bg-slate-200/60 overflow-hidden">
                    <div className={`${p.bar} h-full rounded-full transition-all`} style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px]">
                    <span className="text-slate-500 font-variant-numeric tabular-nums">{(c.count || 0).toLocaleString()} calls</span>
                    <span className={`${p.text} font-semibold inline-flex items-center gap-0.5`}>
                      Drill <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
